/**
 * Conversation statuses are tenant-defined rows in `conversation_status`
 * (Admin → Conversation Management → Status) — labels, colours and the set of
 * selectable statuses all come from there, never from a map in the UI.
 *
 * A conversation still stores the lifecycle enum (`open` / `reopen` /
 * `on_hold` / `closed`) in `conversations.status`, so a catalog row is matched
 * to a conversation by normalising its name down to that same key. Anything the
 * catalog doesn't cover degrades to a humanised form of the raw value rather
 * than to a hardcoded label.
 */
import type { CSSProperties } from 'react'

export interface ConversationStatusOption {
  id: string
  name: string
  /** CSS background authored in admin — a hex colour or a `linear-gradient(…)`. */
  color: string | null
  colorType: string | null
}

/** Spellings tenants use for the same lifecycle bucket. */
const KEY_ALIASES: Record<string, string> = {
  reopened: 'reopen',
  re_open: 'reopen',
  re_opened: 'reopen',
  hold: 'on_hold',
  onhold: 'on_hold',
  resolved: 'closed',
  close: 'closed',
}

/** Normalise a status name or stored enum value to a comparable key. */
export function statusKey(value: string | null | undefined): string {
  const slug = (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
  return KEY_ALIASES[slug] ?? slug
}

/** `open` and `reopen` are one lifecycle position — you can't "re-open" an open conversation. */
export function statusBucket(value: string | null | undefined): string {
  const key = statusKey(value)
  return key === 'reopen' ? 'open' : key
}

/** Fallback label when the catalog has no row for a value: `on_hold` → "On hold". */
export function humanizeStatus(value: string | null | undefined): string {
  const words = (value ?? '')
    .trim()
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
  if (words.length === 0) return '—'
  const [first, ...rest] = words as [string, ...string[]]
  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(' ')
}

export function findStatusOption(
  catalog: ConversationStatusOption[],
  value: string | null | undefined,
): ConversationStatusOption | null {
  const key = statusKey(value)
  if (!key) return null
  return catalog.find((o) => statusKey(o.name) === key) ?? null
}

export interface StatusPresentation {
  label: string
  /** CSS background from the catalog row, or null when unmatched. */
  color: string | null
  /** True when the label came from the catalog rather than the raw value. */
  fromCatalog: boolean
}

export function presentStatus(
  catalog: ConversationStatusOption[],
  value: string | null | undefined,
): StatusPresentation {
  const match = findStatusOption(catalog, value)
  if (match) return { label: match.name, color: match.color, fromCatalog: true }
  return { label: humanizeStatus(value), color: null, fromCatalog: false }
}

/**
 * What a conversation's status looks like on screen.
 *
 * Conversations carry `conversationStatusId` — the catalog row the server
 * linked them to — so the row is looked up by id first and the name/label the
 * API already sent is used when the catalog itself hasn't loaded (or the row
 * was since deleted). Matching the lifecycle enum by name is the last resort,
 * for rows written before the link existed.
 */
export function presentConversationStatus(
  catalog: ConversationStatusOption[],
  conversation: {
    status?: string | null
    conversationStatusId?: string | null
    statusName?: string | null
    statusColor?: string | null
  },
): StatusPresentation {
  const linked = conversation.conversationStatusId
    ? catalog.find((o) => o.id === conversation.conversationStatusId)
    : undefined
  if (linked) return { label: linked.name, color: linked.color, fromCatalog: true }
  if (conversation.statusName) {
    return {
      label: conversation.statusName,
      color: conversation.statusColor ?? null,
      fromCatalog: true,
    }
  }
  return presentStatus(catalog, conversation.status)
}

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i

function toSixDigitHex(color: string): string | null {
  if (!HEX.test(color)) return null
  if (color.length === 7) return color
  const short = color.slice(1)
  return `#${short.split('').map((c) => c + c).join('')}`
}

export interface StatusStyles {
  pill: CSSProperties
  dot: CSSProperties
}

/**
 * The dot carries the tenant's colour verbatim (so gradients survive); the pill
 * only gets a faint tint of it, and only when it's a plain hex — a tenant-picked
 * colour behind the label can't be guaranteed readable.
 */
export function statusStyles(color: string | null): StatusStyles {
  if (!color) return { pill: {}, dot: {} }
  const hex = toSixDigitHex(color.trim())
  return {
    pill: hex ? { backgroundColor: `${hex}1F` } : {},
    dot: { background: color },
  }
}
